import QRCode from "qrcode";
import {
  useState,
  useRef,
  useEffect
} from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { medications as defaultMedications } from "./data/medications";
import { procedures } from "./data/procedures";
import { postOpInstructions } from "./data/postOpInstructions";
import {
  getSuggestedMedications,
  calculatePediatricDose
} from "./data/treatmentEngine";
import {
  evaluateMedicationSafety
} from "./data/medicationSafety";
import {
  useClinic
} from "./context/ClinicContext";
import { useNotifications } from "./notifications/NotificationProvider";
import { requestVexaConfirm } from "./notifications/dialogService";
import {
  db,
  storage
} from "./firebase";

import {
    collection,
    addDoc,
    setDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    query,
    where,
    onSnapshot,
    orderBy,
    updateDoc
} from "./access/firestore";

import {
  ref,
  uploadBytes,
  getDownloadURL
} from "firebase/storage";

import PdfViewerModal from "./PdfViewerModal";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List
} from "lucide-react";

export default function PrescriptionPanelDashboard({
  patient
}) {

  const [selectedPrescription, setSelectedPrescription] = useState([]);
  const [clinicMedications, setClinicMedications] = useState([]);
  const availableMedications = [
    ...defaultMedications.map(medication => {
        const personalizado = clinicMedications.find(
        item => item.medicamentoBase === medication.name
        );

        if (!personalizado) {
        return medication;
        }

        return {
        ...medication,
        ...personalizado,
        adultPresentation:
            personalizado.patientType === "Adulto" &&
            personalizado.presentation
                ? `${personalizado.presentation} ${personalizado.doseValue || ""}${personalizado.doseUnit || ""}`.trim()
                : personalizado.adultPresentation ||
                medication.adultPresentation,

        pediatricPresentation:
            personalizado.pediatricPresentation ||
            medication.pediatricPresentation,

        adultDose:
            personalizado.patientType === "Adulto" &&
            personalizado.doseValue
                ? `${personalizado.doseValue} ${personalizado.doseUnit || ""}`.trim()
                : personalizado.adultDose ||
                medication.adultDose,

        pediatricDoseText:
            personalizado.pediatricDoseText ||
            medication.pediatricDoseText,

        doseMgKg:
            personalizado.doseMgKg ??
            medication.doseMgKg,

        interval:
            personalizado.interval ||
            medication.interval,

        duration:
            personalizado.durationValue
                ? Number(personalizado.durationValue)
                : personalizado.duration ??
                medication.duration,

        route:
            personalizado.route ||
            medication.route,

        allowedRoutes:
            personalizado.allowedRoutes?.length
                ? personalizado.allowedRoutes
                : medication.allowedRoutes,

        contraindications:
            personalizado.contraindications?.length
            ? personalizado.contraindications
            : medication.contraindications,

        allergyContraindications:
            personalizado.allergyContraindications?.length
            ? personalizado.allergyContraindications
            : medication.allergyContraindications,

        warnings:
            personalizado.warnings?.length
            ? personalizado.warnings
            : medication.warnings
        };
    }),

    ...clinicMedications.filter(
        medication => !medication.medicamentoBase
    )
  ];
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Todos");
  const [selectedProcedure, setSelectedProcedure] =
    useState("all");
  const [history, setHistory] =
    useState([]);
  const [
    clinicalAlert,
    setClinicalAlert
  ] = useState(null);  
  const [
    openedPrescription,
    setOpenedPrescription
  ] = useState(null);  
  const {
    clinicId,
    clinicData,
    currentUser
    } = useClinic();

    useEffect(() => {
        if (!clinicId) {
            setClinicMedications([]);
            return;
        }

        const medicamentosRef = collection(
            db,
            "clinics",
            clinicId,
            "medicamentosConfiguracion"
        );

        const unsubscribe = onSnapshot(
            medicamentosRef,
            snapshot => {
            const personalizados = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                origen: "custom"
            }));

            setClinicMedications(personalizados);
            },
            error => {
            console.error(
                "Error cargando medicamentos del consultorio:",
                error
            );
            setClinicMedications([]);
            }
        );

        return () => unsubscribe();
    }, [clinicId]);

  const { notify } = useNotifications();
  const pdfRef =
    useRef(null);    
  const [
        currentInstructions,
        setCurrentInstructions
  ] = useState([]);
  const [
        instructionsHtml,
        setInstructionsHtml
  ] = useState("");
  const instructionsEditor = useEditor({
        extensions: [
            StarterKit,
            Underline
        ],

        content: currentInstructions
            .map(item => `<p>${item}</p>`)
            .join(""),

        onUpdate: ({ editor }) => {
            setInstructionsHtml(
                editor.getHTML()
            );
        }
    });

    useEffect(() => {
        if (!instructionsEditor) return;

        const contenidoActual =
            currentInstructions
                .map(item => `<p>${item}</p>`)
                .join("");

        instructionsEditor.commands.setContent(
            contenidoActual
        );

        setInstructionsHtml(
            contenidoActual
        );
    }, [
        instructionsEditor,
        currentInstructions
  ]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [previewUrl, setPreviewUrl] = useState("");

  const [previewFileName, setPreviewFileName] = useState("");

  const [pdfDownloadUrl, setPdfDownloadUrl] = useState("");

  const [generatingRecipe, setGeneratingRecipe] = useState(false);
  
  const getPrescriptionProcedureId = procedureId => {

        if (
            procedureId === "extraccion_tercer_molar_erupcionado" ||
            procedureId === "extraccion_tercer_molar_semirretenido" ||
            procedureId === "extraccion_tercer_molar_retenido"
        ) {
            return "tercerMolar";
        }

        if (
            procedureId === "extraccion_temporal" ||
            procedureId === "extraccion_permanente" ||
            procedureId === "extraccion_restos_radiculares" ||
            procedureId === "extraccion_quirurgica" ||
            procedureId === "odontectomia"
        ) {
            return "extraccion";
        }

        if (
            procedureId?.startsWith("endodoncia_") ||
            procedureId?.startsWith("retratamiento_") ||
            [
                "apexificacion",
                "apicoformacion",
                "apicectomia"
            ].includes(procedureId)
        ) {
            return "endodoncia";
        }

        if (
            procedureId === "drenaje_absceso"
        ) {
            return "absceso";
        }

        if (
            procedureId === "raspado_cuadrante" ||
            procedureId === "raspado_arcada" ||
            procedureId === "raspado_boca_completa" ||
            procedureId === "curetaje_gingival" ||
            procedureId === "irrigacion_subgingival" ||
            procedureId === "gingivectomia" ||
            procedureId === "gingivoplastia" ||
            procedureId === "frenilectomia" ||
            procedureId === "frenotomia"
        ) {
            return "periodoncia";
        }

        if (
            procedureId === "implante_unitario" ||
            procedureId === "implantes_multiples"
        ) {
            return "implante";
        }

        return null;
  };
  const loadProcedureTreatment =
  procedureId => {

    if (
        procedureId === "all"
    ) {

        setSelectedPrescription([]);

        setCurrentInstructions(
            postOpInstructions.all
        );

        return;

    }

    const prescriptionProcedureId =
        getPrescriptionProcedureId(
            procedureId
        );

    if (!prescriptionProcedureId) {
        setSelectedPrescription([]);
        return;
    }

    const suggested =
        getSuggestedMedications({

            procedure: prescriptionProcedureId,

            allergies:
            currentPatient.alergias || []

    });

    const medicationsToAdd =
        availableMedications
            .filter(medication =>
                suggested.includes(
                    medication.name
                )
            )
            .map(medication => {

                const pediatric =
                    Number(
                        currentPatient.edad
                    ) <= 12;

                let pediatricDoseResult =
                    null;

                if (pediatric) {
                    pediatricDoseResult =
                        calculatePediatricDose({
                            medication,
                            age:
                                currentPatient.edad,
                            weight:
                                currentPatient.peso
                        });
                }

                return {
                    ...medication,

                    customDose:
                        pediatric
                            ? ""
                            : medication.adultDose,

                    customDuration:
                        medication.duration,

                    customRoute:
                        medication.route,

                    customInterval:
                        pediatric &&
                        pediatricDoseResult?.interval
                            ? pediatricDoseResult.interval
                            : medication.interval,

                    customInstructions: "",

                    pediatricDoseResult
                };
            });

    setSelectedPrescription(
      medicationsToAdd
    );

  };  
  const categoryMap = {

    Todos: null,

    AINEs: "AINE",

    Analgésicos: "Analgésico",

    Antibióticos: "Antibiótico",

    Corticoides: "Corticoide",

    Antisépticos: "Antiséptico",

    Antifúngicos: "Antifúngico",

    Antivirales: "Antiviral",

    Ansiolíticos: "Ansiolítico",

    ProtectoresGastricos:
        "Protector gástrico",

    Emergencias:
        "Emergencia",

    Profilaxis:
        "Profilaxis"

  };
  const currentPatient = patient || {

    nombre: "Paciente sin seleccionar",

    edad: "—",

    peso: "—",

    sexo: "—",

    telefono: "No registrado",

    correo: "No registrado",

    direccion: "No registrada"

  };
  const currentDate =

  new Date()
    .toLocaleDateString(
      "es-MX"
  );
  useEffect(() => {

    if (
        !clinicId ||
        !currentPatient?.id
    ) return;

    const q = query(

        collection(
        db,
        "clinics",
        clinicId,
        "prescriptions"
        ),

        where(
        "patientId",
        "==",
        currentPatient.id
        ),

        orderBy(
        "createdAt",
        "desc"
        )

    );

    return onSnapshot(

        q,

        snapshot => {

        setHistory(

            snapshot.docs.map(
            doc => ({

                id: doc.id,

                ...doc.data()

            })
            )

        );

        }

    );

    }, [
    clinicId,
    currentPatient?.id
    ]);
  const alerts = [];
  if (currentPatient.diabetes) {

    alerts.push({
        type: "diabetes",
        text: "Paciente diabético"
    });

    }

    if (currentPatient.hipertension) {

    alerts.push({
        type: "hipertension",
        text: "Paciente con hipertensión"
    });

    }

    if (currentPatient.cardiopatias) {

    alerts.push({
        type: "cardiopatia",
        text: "Paciente con cardiopatías"
    });

    }

    if (currentPatient.anticoagulantes) {

    alerts.push({
        type: "anticoagulantes",
        text: "Paciente bajo tratamiento anticoagulante"
    });

    }

    if (currentPatient.embarazo) {

    alerts.push({
        type: "embarazo",
        text: "Paciente embarazada"
    });

    }

    if (currentPatient.asma) {

    alerts.push({
        type: "asma",
        text: "Paciente asmático"
    });

    }

    if (currentPatient.epilepsia) {

    alerts.push({
        type: "epilepsia",
        text: "Paciente con epilepsia"
    });

    }

    if (currentPatient.hepatitis) {

    alerts.push({
        type: "hepatitis",
        text: "Paciente con hepatitis"
    });

    }

    if (currentPatient.vih) {

    alerts.push({
        type: "vih",
        text: "Paciente con VIH"
    });

    }

    if (currentPatient.osteoporosis) {

    alerts.push({
        type: "osteoporosis",
        text: "Paciente con osteoporosis"
    });

  }
  const isPediatric =
    Number(currentPatient.edad) <= 12;

  const prescribingDoctor = {
        uid:
            currentUser?.uid || "",

        nombre:
            currentUser?.nombre || "",

        puesto:
            currentUser?.puesto || "",

        especialidad:
            currentUser?.especialidad || "",

        cedula:
            currentUser?.cedula || "",

        universidad:
            currentUser?.universidad || "",

        email:
            currentUser?.email || "",

        telefono:
            currentUser?.telefono || "",

        universidadLogoURL:
            currentUser?.universidadLogoURL || ""
  };

  const calculateDose = medication => {

        if (!isPediatric) {

            return medication.adultDose;

        }

        const result =
            medication.pediatricDoseResult;

        if (!result) {

            return "Revisión clínica requerida";

        }

        if (result.blocked) {

            return "Revisión clínica requerida";

        }

        /*
        * ---------------------------------------------------------
        * MG/KG/DOSIS
        * ---------------------------------------------------------
        */

        if (
            result.calculation ===
            "mg/kg/dose"
        ) {

            /*
            * Si existe una dosis en mL,
            * utilizarla directamente.
            */

            if (
                result.doseMl != null
            ) {

                return `${result.doseMl} mL cada ${result.interval}`;

            }

            /*
            * Si no existe presentación líquida,
            * mostrar la dosis calculada en mg.
            */

            if (
                result.doseMgMin != null &&
                result.doseMgMax != null
            ) {

                const doseText =
                    result.doseMgMin ===
                    result.doseMgMax
                        ? `${result.doseMgMin} mg`
                        : `${result.doseMgMin}–${result.doseMgMax} mg`;

                return `${doseText} cada ${result.interval}`;

            }

            return "Revisión clínica requerida";

        }

        /*
        * ---------------------------------------------------------
        * MG/KG/DÍA
        * ---------------------------------------------------------
        */

        if (
            result.calculation ===
            "mg/kg/day"
        ) {

            /*
            * Si existe una dosis en mL,
            * utilizarla directamente.
            */

            if (
                result.doseMl != null
            ) {

                return `${result.doseMl} mL cada ${result.interval}`;

            }

            /*
            * Si no existe presentación líquida,
            * mostrar la dosis calculada en mg.
            */

            if (
                result.doseMgMin != null &&
                result.doseMgMax != null
            ) {

                const doseText =
                    result.doseMgMin ===
                    result.doseMgMax
                        ? `${result.doseMgMin} mg`
                        : `${result.doseMgMin}–${result.doseMgMax} mg`;

                return `${doseText} cada ${result.interval}`;

            }

            return "Revisión clínica requerida";

        }

        /*
        * ---------------------------------------------------------
        * DOSIS FIJA
        * ---------------------------------------------------------
        */

        if (
            result.calculation ===
            "fixed"
        ) {

            return `${result.dose || ""} cada ${result.interval}`;

        }

        /*
        * ---------------------------------------------------------
        * CUALQUIER OTRO CASO
        * ---------------------------------------------------------
        */

        return "Revisión clínica requerida";

  };

  const buildPrescriptionText = medication => {

        const pediatric =
            Number(currentPatient.edad) <= 12;

        const presentation =
            pediatric && medication.pediatricPresentation
                ? medication.pediatricPresentation
                : medication.adultPresentation;

        const duration =
            medication.customDuration ??
            medication.duration;

        const interval =
            medication.customInterval ??
            medication.interval;

        const route =
            medication.customRoute ??
            medication.route;

        const instructions =
            medication.customInstructions?.trim() || "";

        /*
            VÍA INTRAMUSCULAR
        */

        if (route === "IM") {

            return `${medication.name} ${presentation}

            Aplicar dosis única vía intramuscular.

            ${instructions}`;
        }

        /*
            PEDIÁTRICO
        */

        if (pediatric) {

            const result =
                medication.pediatricDoseResult;

            if (!result) {

                return `${medication.name} ${presentation}

            REVISIÓN CLÍNICA REQUERIDA:
            No existe una pauta pediátrica calculada para este medicamento.

            ${instructions}`;
            }

            if (result.blocked) {

                return `${medication.name} ${presentation}

            REVISIÓN CLÍNICA REQUERIDA:
            ${
                result.reason ||
                result.note ||
                "Este medicamento requiere valoración clínica antes de prescribirse."
            }

            ${instructions}`;
            }

            /*
                DOSIS EDITADA POR EL ODONTÓLOGO

                Si el campo Dosis contiene una cantidad
                pediátrica explícita en mL, esa cantidad
                debe ser la que aparezca en la receta.

                Ejemplo:
                "5 mL cada 8h"
                → "Dar 5 mL cada 8h..."
            */

            const customDose =
                String(
                    medication.customDose ?? ""
                ).trim();

            const customDoseIsPediatric =
                /mL\b/i.test(customDose);

            if (customDoseIsPediatric) {

                const doseText =
                    customDose
                        .replace(
                            /cada\s+\S+/i,
                            ""
                        )
                        .trim();

                return `${medication.name} ${presentation}

            Dar ${doseText} cada ${interval} durante ${duration} días.

            ${instructions}`;
            }

            /*
                DOSIS CALCULADA EN mL

                Se utiliza solamente cuando el odontólogo
                todavía no ha introducido una dosis
                pediátrica concreta.
            */

            if (result.doseMl != null) {

                return `${medication.name} ${presentation}

            Dar ${result.doseMl} mL cada ${interval} durante ${duration} días.

            ${instructions}`;
            }

            /*
                COMPATIBILIDAD CON RESULTADOS ANTERIORES
            */

            if (
                result.doseMlMin != null &&
                result.doseMlMax != null
            ) {

                const doseMlText =
                    result.doseMlMin === result.doseMlMax
                        ? `${result.doseMlMin} mL`
                        : `${result.doseMlMin}–${result.doseMlMax} mL`;

                return `${medication.name} ${presentation}

            Dar ${doseMlText} cada ${interval} durante ${duration} días.

            ${instructions}`;
            }

            /*
                DOSIS EN mg/kg/dosis
            */

            if (
                result.calculation === "mg/kg/dose" &&
                result.doseMgMin != null &&
                result.doseMgMax != null
            ) {

                const doseMgText =
                    result.doseMgMin === result.doseMgMax
                        ? `${result.doseMgMin} mg`
                        : `${result.doseMgMin}–${result.doseMgMax} mg`;

                return `${medication.name} ${presentation}

            Dar ${doseMgText} cada ${interval} durante ${duration} días.

            ${instructions}`;
            }

            /*
                DOSIS EN mg/kg/día
            */

            if (
                result.calculation === "mg/kg/day" &&
                result.doseMgMin != null &&
                result.doseMgMax != null
            ) {

                const doseMgText =
                    result.doseMgMin === result.doseMgMax
                        ? `${result.doseMgMin} mg`
                        : `${result.doseMgMin}–${result.doseMgMax} mg`;

                return `${medication.name} ${presentation}

            Dar ${doseMgText} cada ${interval} durante ${duration} días.

            ${instructions}`;
            }

            /*
                DOSIS FIJA
            */

            if (
                result.calculation === "fixed"
            ) {

                return `${medication.name} ${presentation}

            ${result.dose || ""} cada ${interval} durante ${duration} días.

            ${instructions}`;
            }

            /*
                CUALQUIER OTRO CASO
            */

            return `${medication.name} ${presentation}

            REVISIÓN CLÍNICA REQUERIDA:
            La pauta pediátrica de este medicamento requiere valoración clínica.

            ${instructions}`;
        }

        /*
            ADULTO
        */

        const customDose =
            medication.customDose ??
            medication.adultDose ??
            "";

        const doseText =
            String(customDose)
                .replace(
                    /cada\s+\S+/i,
                    ""
                )
                .trim();

        return `${medication.name} ${presentation}

            Tomar ${doseText} cada ${interval} durante ${duration} días.

            ${instructions}`;
  };

  const buildPharmacyText = medication => {

        const pediatric =
            Number(currentPatient.edad) <= 12;

        const presentation =
            pediatric && medication.pediatricPresentation
                ? medication.pediatricPresentation
                : medication.adultPresentation;

        const duration =
            medication.customDuration ??
            medication.duration;

        const interval =
            medication.customInterval ??
            medication.interval;

        const route =
            medication.customRoute ??
            medication.route;

        const instructions =
            medication.customInstructions?.trim() || "";

        /*
            ENJUAGUE
        */

        if (
            medication.customAdministration ===
            "enjuague"
        ) {

            const dose =
                medication.customDose ??
                medication.adultDose ??
                "";

            return `${medication.name} ${presentation}

    Realizar enjuagues con ${dose} durante 30 segundos cada ${interval} durante ${duration} días.

    No ingerir.

    ${instructions}`;
        }

        /*
            VÍA INTRAMUSCULAR
        */

        if (route === "IM") {

            return `${medication.name} ${presentation}

    Aplicar dosis única vía intramuscular.

    ${instructions}`;
        }

        /*
            PEDIÁTRICO
        */

        if (pediatric) {

            const result =
                medication.pediatricDoseResult;

            if (!result) {

                return `${medication.name} ${presentation}

    REVISIÓN CLÍNICA REQUERIDA:
    No existe una pauta pediátrica calculada para este medicamento.

    ${instructions}`;
            }

            if (result.blocked) {

                return `${medication.name} ${presentation}

    REVISIÓN CLÍNICA REQUERIDA:
    ${
        result.reason ||
        result.note ||
        "Este medicamento requiere valoración clínica antes de prescribirse."
    }

    ${instructions}`;
            }

            /*
                DOSIS EN mL
            */

            const customDose =
                medication.customDose?.trim();

            if (customDose) {

                const doseText =
                    customDose
                        .replace(
                            /cada\s+\S+/i,
                            ""
                        )
                        .trim();

                return `${medication.name} ${presentation}

                Dar ${doseText} cada ${interval} durante ${duration} días.

                ${instructions}`;
            }

            if (
                result.doseMlMin != null &&
                result.doseMlMax != null
            ) {

                const doseMlText =
                    result.doseMlMin === result.doseMlMax
                        ? `${result.doseMlMin} mL`
                        : `${result.doseMlMin}–${result.doseMlMax} mL`;

                return `${medication.name} ${presentation}

                Dar ${doseMlText} cada ${interval} durante ${duration} días.

                ${instructions}`;
            }

            /*
                DOSIS EN mg
            */

            if (
                result.doseMgMin != null &&
                result.doseMgMax != null
            ) {

                const doseMgText =
                    result.doseMgMin === result.doseMgMax
                        ? `${result.doseMgMin} mg`
                        : `${result.doseMgMin}–${result.doseMgMax} mg`;

                return `${medication.name} ${presentation}

    Dar ${doseMgText} cada ${interval} durante ${duration} días.

    ${instructions}`;
            }

            /*
                DOSIS FIJA
            */

            if (
                result.calculation === "fixed"
            ) {

                return `${medication.name} ${presentation}

    ${result.dose || ""} cada ${interval} durante ${duration} días.

    ${instructions}`;
            }

            return `${medication.name} ${presentation}

    REVISIÓN CLÍNICA REQUERIDA:
    La pauta pediátrica de este medicamento requiere valoración clínica.

    ${instructions}`;
        }

        /*
            ADULTO
        */

        const customDose =
            medication.customDose ??
            medication.adultDose ??
            "";

        const doseText =
            String(customDose)
                .replace(
                    /cada\s+\S+/i,
                    ""
                )
                .trim();

        return `${medication.name} ${presentation}

    Tomar ${doseText} cada ${interval} durante ${duration} días.

    ${instructions}`;
  };

  const buildPatientText = medication => {

        return buildPrescriptionText(
            medication
        );

  };

  const getPharmacyLines = () => {

        return selectedPrescription.map(medication => {

            const text = buildPharmacyText(medication);

            if (typeof text !== "string") {

                console.error(
                    "buildPharmacyText devolvió:",
                    text,
                    medication
                );

                return JSON.stringify(text);

            }

            return text;

        });

    };

    const getPatientLines = () => {

        return selectedPrescription.map(medication => {

            const text = buildPatientText(medication);

            if (typeof text !== "string") {

                console.error(
                    "buildPatientText devolvió:",
                    text,
                    medication
                );

                return JSON.stringify(text);

            }

            return text;

        });

    };

  const loadImageAsBase64 = url => {

    return new Promise(
        (resolve, reject) => {

        const img = new Image();

        img.crossOrigin = "Anonymous";

        img.onload = () => {

            const canvas =
            document.createElement(
                "canvas"
            );

            canvas.width =
            img.width;

            canvas.height =
            img.height;

            const ctx =
            canvas.getContext("2d");

            ctx.drawImage(
            img,
            0,
            0
            );

            resolve(
            canvas.toDataURL(
                "image/png"
            )
            );

        };

        img.onerror =
            reject;

        img.src =
            url;

        }
    );

  };

  const filteredMedications =
    availableMedications.filter(medication => {

        const matchesSearch =
            medication.name
            .toLowerCase()
            .includes(
                search.toLowerCase()
            );

        const matchesCategory =
            !categoryMap[category]
                ? true
                : medication.category === categoryMap[category];

        return (
            matchesSearch &&
            matchesCategory
        );

        }
    );

    const alergiasPaciente = Array.isArray(currentPatient.alergias)
        ? currentPatient.alergias
        : typeof currentPatient.alergias === "string" &&
          currentPatient.alergias.trim() &&
          currentPatient.alergias.trim().toLowerCase() !== "ninguna"
            ? [currentPatient.alergias.trim()]
            : [];

    alergiasPaciente.forEach(allergy => {
        alerts.push({
            type: "allergy",
            text: `Paciente alérgico a ${allergy}`
        });
    });

    if (
    currentPatient.edad !== "—" &&
    currentPatient.edad <= 12
    ) {

    alerts.push({
        type: "pediatric",
        text: "Paciente pediátrico"
    });

    }

    if (
    !currentPatient.peso ||
    currentPatient.peso === "—"
    ) {

    alerts.push({
        type: "weight",
        text: "Peso no registrado"
    });

  }

  const evaluateMedicationSafety = (
        medication,
        patient
    ) => {

        const warnings = [];

        let blocked = false;

        const alternatives = [];

        const name =
            medication.name.toLowerCase();

        /*
        ALERGIA A PENICILINA
        */

        const alergiasParaValidar = Array.isArray(patient.alergias)
            ? patient.alergias
            : typeof patient.alergias === "string"
                ? [patient.alergias]
                : [];

        if (
            alergiasParaValidar.some(allergy =>
                String(allergy).toLowerCase().includes("penicil")
            )
        ) {

            if (
                name.includes(
                    "amoxicilina"
                )
            ) {

                blocked = true;

                warnings.push(
                    "Alergia a penicilina"
                );

                alternatives.push(
                    "Clindamicina",
                    "Azitromicina",
                    "Claritromicina"
                );

            }

        }

        /*
        ANTICOAGULANTES
        */

        if (
            patient.anticoagulantes
        ) {

            if (

                [
                    "ibuprofeno",
                    "naproxeno",
                    "diclofenaco",
                    "ketorolaco"
                ].includes(name)

            ) {

                blocked = true;

                warnings.push(
                    "Riesgo aumentado de sangrado por anticoagulantes"
                );

                alternatives.push(
                    "Paracetamol"
                );

            }

        }

        /*
        DIABETES
        */

        if (
            patient.diabetes
        ) {

            if (

                name.includes(
                    "dexametasona"
                ) ||

                name.includes(
                    "betametasona"
                )

            ) {

                warnings.push(
                    "Puede elevar la glucosa sanguínea"
                );

                alternatives.push(
                    "Ibuprofeno",
                    "Paracetamol"
                );

            }

        }

        /*
        HIPERTENSIÓN
        */

        if (
            patient.hipertension
        ) {

            if (

                name.includes(
                    "ibuprofeno"
                ) ||

                name.includes(
                    "naproxeno"
                ) ||

                name.includes(
                    "diclofenaco"
                )

            ) {

                warnings.push(
                    "Los AINEs pueden elevar la presión arterial"
                );

                alternatives.push(
                    "Paracetamol"
                );

            }

        }

        /*
        EMBARAZO
        */

        if (
            patient.embarazo
        ) {

            if (

                name.includes(
                    "naproxeno"
                ) ||

                name.includes(
                    "diclofenaco"
                )

            ) {

                warnings.push(
                    "Evitar durante embarazo salvo indicación médica"
                );

                alternatives.push(
                    "Paracetamol"
                );

            }

        }

        return {

            blocked,

            warnings,

            alternatives

        };

  };  

  const getMedicationStatus =
    medication => {

        const safety =
        evaluateMedicationSafety(
            medication,
            currentPatient
        );

        if (safety.blocked) {

        return {
            color: "red",
            warnings:
            safety.warnings
        };

        }

        if (
        safety.warnings.length > 0
        ) {

        return {
            color: "yellow",
            warnings:
            safety.warnings
        };

        }

        return {
        color: "green",
        warnings: []
        };

    };
  const addMedicationDirect = (
        medication
    ) => {

        const exists =
            selectedPrescription.find(
                item =>
                    item.id === medication.id
            );

        if (exists) return;

        let prescriptionMedication = {

            ...medication,

            customDose:
                isPediatric
                    ? ""
                    : medication.adultDose,

            customInterval:
                medication.interval,

            customDuration:
                medication.duration,

            customRoute:
                medication.route,

            customInstructions: ""

        };

        /*
            ---------------------------------------------------------
            CÁLCULO PEDIÁTRICO
            ---------------------------------------------------------

            El motor pediátrico es ahora la única fuente
            para determinar dosis, frecuencia, duración
            y presentación pediátrica.
        */

        if (isPediatric) {

            const pediatricDoseResult =
                calculatePediatricDose({

                    medication,

                    age:
                        currentPatient.edad,

                    weight:
                        currentPatient.peso

                });

            prescriptionMedication = {

                ...prescriptionMedication,

                pediatricDoseResult

            };

            /*
                Si el motor determina una frecuencia,
                utilizarla como valor inicial editable.
            */

            if (
                pediatricDoseResult &&
                !pediatricDoseResult.blocked &&
                pediatricDoseResult.interval
            ) {

                prescriptionMedication.customInterval =
                    pediatricDoseResult.interval;

            }

            /*
                Si el motor determina una duración,
                utilizarla como valor inicial editable.
            */

            if (
                pediatricDoseResult &&
                !pediatricDoseResult.blocked &&
                pediatricDoseResult.duration != null
            ) {

                prescriptionMedication.customDuration =
                    pediatricDoseResult.duration;

            }

        }

        setSelectedPrescription(
            prev => [

                ...prev,

                prescriptionMedication

            ]
        );

  };

    const addMedication = (
        medication
        ) => {

        const safety =
            evaluateMedicationSafety(
            medication,
            currentPatient
            );

        if (safety.blocked) {

            setClinicalAlert({

                type: "blocked",

                medication,

                warnings:
                    safety.warnings,

                alternatives:
                    safety.alternatives

            });

            return;

        }

        if (
            safety.warnings.length > 0
        ) {

            setClinicalAlert({

                type: "warning",

                medication,

                warnings: safety.warnings,

                alternatives:
                    safety.alternatives

            });

            return;

        }

        addMedicationDirect(
            medication
        );

      };

    const generatePDF = async () => {

        setGeneratingRecipe(true);


        // ==========================
        // GUARDAR LA RECETA PRIMERO
        // ==========================

            const saved = await savePrescription();

                if (!saved) {
                    setGeneratingRecipe(false);
                    return;
                }

            const prescriptionVerificationUrl =
                `https://app.vexadent.com/verificar-receta/${saved.id}`;

            let prescriptionQrDataUrl = "";

            try {

                prescriptionQrDataUrl =
                    await QRCode.toDataURL(
                        prescriptionVerificationUrl,
                        {
                            width: 300,
                            margin: 2,
                            errorCorrectionLevel: "M"
                        }
                    );

            } catch (error) {

                console.error(
                    "Error generando QR de verificación de receta:",
                    error
                );

            }

        let logoBase64 =
            clinicData?.logoBase64 || null;

        let universityLogoBase64 =
            currentUser?.universidadLogoBase64 || null;

        try {

            if (
                !logoBase64 &&
                clinicData?.logoURL
            ) {

                logoBase64 =
                    await loadImageAsBase64(
                        clinicData.logoURL
                    );

            }

            if (

                !universityLogoBase64 &&

                currentUser?.universidadLogoURL

            ) {

                universityLogoBase64 =
                    await loadImageAsBase64(
                        currentUser.universidadLogoURL
                    );

            }

        } catch(err) {

            console.error(
                "Error cargando logo:",
                err
            );

        }

        const doc = new jsPDF();

        try {

        /*
        PÁGINA 1
        FARMACIA
        */

        let y = 20;

        if (logoBase64) {

            doc.addImage(
                logoBase64,
                "PNG",
                20,
                15,
                25,
                25
            );

            if (universityLogoBase64) {

            doc.addImage(
                universityLogoBase64,
                "PNG",
                165,
                15,
                25,
                25
            );

            }

            }

            doc.setFontSize(18);

            console.log("nombreClinica:", clinicData?.nombreClinica, typeof clinicData?.nombreClinica);
            console.log("telefono:", clinicData?.telefono, typeof clinicData?.telefono);
            console.log("direccion:", clinicData?.direccion, typeof clinicData?.direccion);
            console.log("currentUser:", currentUser);

            doc.text(
            clinicData?.nombreClinica ||
            "Consultorio",
            55,
            20
            );

            doc.setFontSize(8);

            doc.text(
            clinicData?.telefono || "",
            55,
            27
            );

            const direccionTexto =
                typeof clinicData?.direccion === "string"
                    ? clinicData.direccion
                    : [
                        clinicData?.direccion?.calle,
                        clinicData?.direccion?.numeroExterior,
                        clinicData?.direccion?.numeroInterior
                            ? `Int. ${clinicData.direccion.numeroInterior}`
                            : null,
                        clinicData?.direccion?.colonia,
                        clinicData?.direccion?.ciudad,
                        clinicData?.direccion?.estado,
                        clinicData?.direccion?.codigoPostal
                            ? `C.P. ${clinicData.direccion.codigoPostal}`
                            : null
                    ]
                    .filter(Boolean)
                    .join(", ");

            doc.text(
                direccionTexto,
                55,
                32
            );

            doc.text(
            `${currentUser?.nombre || "Odontólogo"} | Cédula: ${
                currentUser?.cedula || "Sin registrar"
            }`,
            55,
            38
            );

            doc.setDrawColor(180);

            doc.line(
            20,
            45,
            190,
            45
            );

            y = 55;

            doc.setFontSize(11);

        doc.setFontSize(11);

        doc.text(
            `Paciente: ${currentPatient.nombre}`,
            20,
            y
        );

        y += 7;

        doc.text(
            `Edad: ${currentPatient.edad} años`,
            20,
            y
        );

        y += 7;

        doc.text(
            `Peso: ${currentPatient.peso} kg`,
            20,
            y
        );

        y += 12;

        doc.text(
            `Fecha: ${currentDate}`,
            20,
            y
        );

        y += 10;

        doc.setFontSize(14);

        doc.setFontSize(14);

        doc.text(
            "RECETA",
            20,
            y
        );

        y += 10;

        getPharmacyLines().forEach(
            text => {

                const lines =
                    doc.splitTextToSize(
                        typeof text === "string"
                            ? text
                            : JSON.stringify(text),
                        165
                    );

                doc.text(
                    lines,
                    20,
                    y
                );

                y +=
                    lines.length * 6 +
                    8;

            }
        );

        y += 15;

        doc.line(
            120,
            y,
            190,
            y
        );

        y += 6;

        doc.text(
            "Firma",
            130,
            y
        );

        if (prescriptionQrDataUrl) {

            const qrSize = 28;

            doc.addImage(
                prescriptionQrDataUrl,
                "PNG",
                20,
                y - qrSize - 5,
                qrSize,
                qrSize
            );

            doc.setFontSize(7);

            doc.text(
                "Datos del profesional emisor",
                20,
                y + 2
            );

        }

        /*
        PÁGINA 2
        PACIENTE
        */

        doc.addPage();

        if (logoBase64) {

        doc.addImage(
            logoBase64,
            "PNG",
            20,
            15,
            25,
            25
        );

        if (universityLogoBase64) {

        doc.addImage(
            universityLogoBase64,
            "PNG",
            165,
            15,
            25,
            25
        );

        }

        }

        doc.setFontSize(18);

        doc.text(
        clinicData?.nombreClinica ||
        "Consultorio",
        55,
        20
        );

        doc.setFontSize(8);

        doc.text(
        clinicData?.telefono || "",
        55,
        27
        );

        doc.text(
            direccionTexto,
            55,
            32
        );

        doc.text(
        `${currentUser?.nombre || "Odontólogo"} | Cédula: ${
            currentUser?.cedula || "Sin registrar"
        }`,
        55,
        38
        );

        doc.setDrawColor(180);

        doc.line(
        20,
        45,
        190,
        45
        );

        y = 55;

        doc.setFontSize(11);

        doc.setFontSize(11);

        doc.text(
            `Paciente: ${currentPatient.nombre}`,
            20,
            y
        );

        y += 7;

        doc.text(
            `Edad: ${currentPatient.edad} años`,
            20,
            y
        );

        y += 7;

        doc.text(
            `Peso: ${currentPatient.peso} kg`,
            20,
            y
        );

        y += 12;

        doc.text(
            `Fecha: ${currentDate}`,
            20,
            y
        );

        y += 10;

        doc.setFontSize(14);

        doc.text(
            "COPIA DE RECETA PARA PACIENTE",
            20,
            y
        );

        y += 10;

        getPatientLines().forEach(
            text => {

                const lines =
                    doc.splitTextToSize(
                        typeof text === "string"
                            ? text
                            : JSON.stringify(text),
                        165
                    );

                doc.text(
                    lines,
                    20,
                    y
                );

                y +=
                    lines.length * 6 +
                    8;

            }
        );

        y += 10;

        doc.setFontSize(13);

        doc.text(
            "Indicaciones postoperatorias",
            20,
            y
        );

        y += 10;

        console.log(
            "instructionsHtml:",
            instructionsHtml
        );

        if (instructionsHtml) {

            const instructionsContainer =
                document.createElement("div");

            instructionsContainer.style.width = "650px";
            instructionsContainer.style.padding = "0 0 12px 0";
            instructionsContainer.style.margin = "0";
            instructionsContainer.style.fontFamily =
                "Arial, sans-serif";
            instructionsContainer.style.fontSize = "14px";
            instructionsContainer.style.lineHeight = "1.6";
            instructionsContainer.style.color = "#374151";

            instructionsContainer.innerHTML =
                instructionsHtml;

            instructionsContainer.style.listStyleType = "disc";

            instructionsContainer.querySelectorAll("ul").forEach(ul => {
                ul.style.listStyleType = "none";
                ul.style.paddingLeft = "0";
                ul.style.marginTop = "6px";
                ul.style.marginBottom = "6px";
            });

            instructionsContainer.querySelectorAll("li").forEach(li => {

                li.style.listStyleType = "none";
                li.style.marginBottom = "4px";
                li.style.paddingLeft = "0";

                const bullet =
                    document.createElement("span");

                bullet.textContent = "• ";
                bullet.style.display = "inline-block";
                bullet.style.width = "18px";

                const firstParagraph =
                    li.querySelector("p");

                if (firstParagraph) {
                    firstParagraph.insertBefore(
                        bullet,
                        firstParagraph.firstChild
                    );
                } else {
                    li.insertBefore(
                        bullet,
                        li.firstChild
                    );
                }
            });

            instructionsContainer.querySelectorAll("p").forEach(p => {
                p.style.marginTop = "0";
                p.style.marginBottom = "6px";
            });

            document.body.appendChild(
                instructionsContainer
            );

            await new Promise(resolve =>
                setTimeout(resolve, 50)
            );

            const canvas =
                await html2canvas(
                    instructionsContainer,
                    {
                        scale: 1.5,
                        useCORS: true,
                        backgroundColor: "#ffffff"
                    }
                );

            document.body.removeChild(
                instructionsContainer
            );

            const imgData =
                canvas.toDataURL("image/png");

            const pdfWidth = 170;

            const pdfHeight =
                (canvas.height * pdfWidth) /
                canvas.width;

            const pageHeight =
                doc.internal.pageSize.getHeight();

            const marginBottom = 20;

            if (
                y + pdfHeight >
                pageHeight - marginBottom
            ) {
                doc.addPage();
                y = 20;
            }

            doc.addImage(
                imgData,
                "PNG",
                20,
                y,
                pdfWidth,
                pdfHeight
            );

            y += pdfHeight;
        }

        y += 20;

        doc.line(
            120,
            y,
            190,
            y
        );

        y += 6;

        doc.text(
            "Firma",
            130,
            y
        );

        const pdfBlob = doc.output("blob");

        const storageRef = ref(

            storage,

            `clinics/${clinicId}/recipes/${Date.now()}_${currentPatient.id}.pdf`

        );

        await uploadBytes(

            storageRef,

            pdfBlob

        );

        const downloadUrl =
            await getDownloadURL(storageRef);

        await updateDoc(
            saved,
            {
                pdfUrl: downloadUrl
            }
        );

        setPdfDownloadUrl(downloadUrl);

        console.log("PDF URL:", downloadUrl);

        const pdfUrl = URL.createObjectURL(pdfBlob);

        const fileName = `Receta_${currentPatient.nombre}.pdf`;

        setPreviewUrl(pdfUrl);
        setPreviewFileName(fileName);
        setPreviewOpen(true);
        setGeneratingRecipe(false);

        }
        catch(err){

            console.error("ERROR PDF");

            console.error(err);

            console.error(err.stack);

            setGeneratingRecipe(false);

            throw err;
        }

    };

    const savePrescription = async () => {

        try {

            // ==========================
            // VALIDAR CLÍNICA
            // ==========================

            if (!clinicId) {

                notify({

                    type: "error",

                    message: "No se encontró la clínica."

                });

                return false;

            }

            // ==========================
            // VALIDAR PACIENTE
            // ==========================

            if (!currentPatient?.id) {

                notify({

                    type: "warning",

                    message: "Seleccione un paciente."

                });

                return false;

            }

            // ==========================
            // VALIDAR MEDICAMENTOS
            // ==========================

            if (

                selectedPrescription.length === 0

            ) {

                notify({

                    type: "warning",

                    message: "Agregue al menos un medicamento."

                });

                return false;

            }

            // ==========================
            // GUARDAR EN FIRESTORE
            // ==========================

            const prescriptionRef = await addDoc(
            collection(
                db,
                "clinics",
                clinicId,
                "prescriptions"
            ),

                {

                    patientId:
                        currentPatient.id,

                    patientName:
                        currentPatient.nombre,

                    procedure:
                        selectedProcedure === "all"
                            ? null
                            : selectedProcedure,

                    medications:
                        selectedPrescription.map(medication =>
                            Object.fromEntries(
                                Object.entries(medication)
                                    .filter(
                                        ([, value]) =>
                                            value !== undefined
                                    )
                            )
                        ),

                    instructions:
                        currentInstructions || [],

                    doctorId:
                        currentUser?.id || "",

                    doctorName:
                        currentUser?.nombre || "",

                    createdAt:
                        serverTimestamp()

                }

            );

            await setDoc(
                doc(
                    db,
                    "prescriptionVerifications",
                    prescriptionRef.id
                ),
                {

                    clinicId,
                    prescriptionId:
                        prescriptionRef.id,

                    doctorId:
                        currentUser?.id || "",

                    doctorName:
                        currentUser?.nombre || "",

                    puesto:
                        currentUser?.puesto || "",

                    especialidad:
                        currentUser?.especialidad || "",

                    cedula:
                        currentUser?.cedula || "",

                    universidad:
                        currentUser?.universidad || "",

                    email:
                        currentUser?.email || "",

                    telefono:
                        currentUser?.telefono || "",

                    universidadLogoURL:
                        currentUser?.universidadLogoURL || "",

                    clinicName:
                        clinicData?.nombreClinica ||
                        clinicData?.nombre ||
                        "",

                    clinicAddress:
                        clinicData?.direccion ||
                        clinicData?.direccionClinica ||
                        "",

                    clinicPhone:
                        clinicData?.telefono ||
                        clinicData?.telefonoClinica ||
                        "",

                    clinicLogoURL:
                        clinicData?.logoURL ||
                        "",

                    createdAtText:
                        new Date().toLocaleString(
                            "es-MX",
                            {
                                dateStyle: "long",
                                timeStyle: "short"
                            }
                        ),

                    verified:
                        true

                }

            );

            notify({

                type: "success",

                message: "Receta guardada."

            });

            return prescriptionRef;

        }

        catch (err) {

            console.error(err);

            notify({

                type: "error",

                message: "Error al guardar la receta."

            });

            return false;

        }

    };

    const deletePrescription = async (prescriptionId) => {

        console.log("========== ELIMINAR RECETA ==========");
        console.log("clinicId:", clinicId);
        console.log("prescriptionId:", prescriptionId);
        console.log("currentPatient:", currentPatient);
        console.log("currentUser:", currentUser);

        try {

            await deleteDoc(

                doc(

                    db,

                    "clinics",

                    clinicId,

                    "prescriptions",

                    prescriptionId

                )

            );

            console.log("✅ Receta eliminada correctamente.");

            notify({

                type: "success",

                message: "Receta eliminada."

            });

        } catch (err) {

            console.error("❌ ERROR ELIMINANDO RECETA");
            console.error("Código:", err.code);
            console.error("Mensaje:", err.message);
            console.error(err);

            notify({

                type: "error",

                message: "Error eliminando la receta."

            });

        }

    };

  return (

    <div className="min-h-screen bg-slate-100 p-6 w-full">

      <div className="w-full max-w-none rounded-3xl bg-white shadow-xl overflow-hidden">

        {/* HEADER */}

        <div className="border-b bg-slate-900 px-6 py-5 text-white">

          <h1 className="text-xl font-bold">

            Panel de Recetas

          </h1>

          <p className="mt-2 text-slate-300">

            Dashboard de Prescripción

          </p>

        </div>

        {/* PROCEDIMIENTO */}

        <div className="border-b bg-white px-6 py-4">

            <div className="flex items-center gap-4">

                <div className="shrink-0">

                    <div className="text-sm font-semibold text-slate-800">
                        Procedimiento
                    </div>

                </div>

                <select
                    value={selectedProcedure}
                    onChange={e => {

                        const procedureId = e.target.value;

                        setSelectedProcedure(procedureId);

                        setCurrentInstructions(
                            postOpInstructions[procedureId] || []
                        );

                        loadProcedureTreatment(
                            procedureId
                        );

                    }}
                    className="
                        flex-1
                        rounded-xl
                        border
                        border-slate-200
                        bg-white
                        px-3
                        py-2.5
                        text-sm
                        text-slate-700
                        outline-none
                        focus:border-blue-500
                    "
                >

                    <option value="all">
                        Seleccionar procedimiento...
                    </option>

                    {procedures.flatMap(specialty =>
                        specialty.categories.flatMap(category =>
                            category.procedures
                            .filter(procedure =>
                                getPrescriptionProcedureId(procedure.id)
                            )
                            .map(procedure => (
                                <option
                                key={procedure.id}
                                value={procedure.id}
                                >
                                {specialty.specialty} — {category.name} — {procedure.name}
                                </option>
                            ))
                        )
                    )}

                </select>

            </div>

        </div>

        {/* DASHBOARD */}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 p-3 sm:p-6 min-w-0">

        {/* CABECERA DEL PACIENTE */}
        <div className="col-span-12">
            <div
                style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 20,
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
                }}
            >
                <div>
                <div
                    style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 4
                    }}
                >
                    PACIENTE
                </div>

                <div
                    style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#111827"
                    }}
                >
                    {currentPatient?.nombreCompleto ||
                    currentPatient?.nombre ||
                    "Paciente"}
                </div>
                </div>

                <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 28,
                    flexWrap: "wrap"
                }}
                >
                <div>
                    <div
                    style={{
                        fontSize: 11,
                        color: "#6b7280"
                    }}
                    >
                    EDAD
                    </div>
                    <strong>
                    {currentPatient?.edad || "—"}
                    </strong>
                </div>

                <div>
                    <div
                    style={{
                        fontSize: 11,
                        color: "#6b7280"
                    }}
                    >
                    PESO
                    </div>
                    <strong>
                    {currentPatient?.peso
                        ? `${currentPatient.peso} kg`
                        : "—"}
                    </strong>
                </div>

                <div>
                    <div
                    style={{
                        fontSize: 11,
                        color: "#6b7280"
                    }}
                    >
                    ALERGIAS
                    </div>
                    <strong>
                    {currentPatient?.alergias || "Ninguna registrada"}
                    </strong>
                </div>
                </div>
            </div>
        </div>

          {/* CATÁLOGO */}

          <div className="col-span-12 lg:col-span-4 min-w-0">

            <div
                className="
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    p-3
                    sm:p-4
                    shadow-sm
                    h-auto
                    lg:h-[900px]
                    flex
                    flex-col
                    min-w-0
                "
            >

              <h2 className="text-sm font-bold">

                Catálogo

              </h2>

              <div className="mt-4">

                <input
                    value={search}
                    onChange={e =>
                        setSearch(e.target.value)
                    }
                    placeholder="Buscar medicamento..."
                    className="
                    w-full
                    rounded-xl
                    border
                    p-3
                    text-sm
                    "
                />

                </div>

                <div className="mt-4 flex flex-wrap gap-2">

                    <button
                        onClick={() =>
                        setCategory("Todos")
                        }
                        className={
                        category === "Todos"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                    >
                        Todos
                    </button>

                    <button
                        onClick={() =>
                        setCategory("AINEs")
                        }
                        className={
                        category === "AINEs"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                    >
                        AINEs
                    </button>

                    <button
                        onClick={() =>
                        setCategory("Antibióticos")
                        }
                        className={
                        category === "Antibióticos"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                    >
                        Antibióticos
                    </button>

                    <button
                        onClick={() =>
                            setCategory("Analgésicos")
                        }
                        className={
                            category === "Analgésicos"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                        >
                        Analgésicos
                        </button>

                        <button
                        onClick={() =>
                            setCategory("Corticoides")
                        }
                        className={
                            category === "Corticoides"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                        >
                        Corticoides
                        </button>

                        <button
                        onClick={() =>
                            setCategory("Antisépticos")
                        }
                        className={
                            category === "Antisépticos"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                        >
                        Antisépticos
                    </button>

                    <button
                        onClick={() =>
                            setCategory("Antifúngicos")
                        }
                        className={
                            category === "Antifúngicos"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                    >
                        Antifúngicos
                    </button>

                    <button
                        onClick={() =>
                            setCategory("Antivirales")
                        }
                        className={
                            category === "Antivirales"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                    >
                        Antivirales
                    </button>

                    <button
                        onClick={() =>
                            setCategory("Ansiolíticos")
                        }
                        className={
                            category === "Ansiolíticos"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                    >
                        Ansiolíticos
                    </button>

                    <button
                        onClick={() =>
                            setCategory(
                                "ProtectoresGastricos"
                            )
                        }
                        className={
                            category ===
                            "ProtectoresGastricos"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                    >
                        Protectores
                    </button>

                    <button
                        onClick={() =>
                            setCategory("Emergencias")
                        }
                        className={
                            category === "Emergencias"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                    >
                        Emergencias
                    </button>

                    <button
                        onClick={() =>
                            setCategory("Profilaxis")
                        }
                        className={
                            category === "Profilaxis"
                            ? "rounded-xl bg-blue-100 px-3 py-2 text-xs"
                            : "rounded-xl bg-gray-100 px-3 py-2 text-xs"
                        }
                    >
                        Profilaxis
                    </button>

                </div>

                <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                        Medicamentos disponibles
                    </span>

                    <span className="text-xs font-semibold text-slate-700">
                        {filteredMedications.length}
                    </span>
                </div>

                <div
                    className="
                        mt-4
                        space-y-3
                        overflow-y-auto
                        flex-1
                        min-w-0
                        max-h-[420px]
                        lg:max-h-none
                        pr-1
                        sm:pr-2
                    "
                >

                    {
                        filteredMedications.map((medication) => {
                            const status =
                            getMedicationStatus(medication);

                            return (
                            <div
                                key={medication.id}
                                className="
                                rounded-2xl
                                border
                                bg-white
                                p-4
                                hover:shadow-sm
                                transition
                                "
                            >
                                <div className="flex items-start justify-between gap-3">

                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-slate-900">
                                    {medication.name}
                                    </div>

                                    <div className="mt-1 text-xs text-slate-500">
                                        {isPediatric && medication.pediatricPresentation
                                            ? `Presentación pediátrica: ${medication.pediatricPresentation}`
                                            : medication.adultPresentation ||
                                            medication.presentation ||
                                            "Sin presentación"}
                                    </div>
                                </div>

                                <span
                                    className={
                                    status.color === "green"
                                        ? "rounded-full bg-green-100 px-2 py-1 text-[10px] font-semibold text-green-700"
                                        : status.color === "yellow"
                                        ? "rounded-full bg-yellow-100 px-2 py-1 text-[10px] font-semibold text-yellow-700"
                                        : "rounded-full bg-red-100 px-2 py-1 text-[10px] font-semibold text-red-700"
                                    }
                                >
                                    {status.color === "green"
                                    ? "Seguro"
                                    : status.color === "yellow"
                                        ? "Precaución"
                                        : "Contraindicado"}
                                </span>

                                </div>

                                <button
                                type="button"
                                onClick={() =>
                                    addMedication(medication)
                                }
                                className="
                                    mt-3
                                    w-full
                                    rounded-xl
                                    bg-blue-600
                                    px-3
                                    py-2
                                    text-xs
                                    font-medium
                                    text-white
                                    hover:bg-blue-700
                                "
                                >
                                Agregar
                                </button>
                            </div>
                            );
                        })
                    }

                </div>

            </div>

          </div>

        {/* RECETA */}

        <div className="col-span-12 lg:col-span-8 min-w-0">

        <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm min-h-0 lg:min-h-[900px] min-w-0">

            <div className="flex items-start justify-between gap-4">

                <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                        Receta electrónica
                    </h2>

                    <div className="mt-1 text-xs text-slate-500">
                        Construye y revisa la prescripción del paciente
                    </div>
                </div>

                {openedPrescription && (

                    <div
                        className="
                            shrink-0
                            rounded-xl
                            bg-blue-50
                            px-3
                            py-2
                            text-xs
                            text-blue-700
                        "
                    >
                        <div className="flex items-center gap-3">

                            <div>
                                <div className="font-semibold">
                                    Receta histórica
                                </div>

                                <div className="mt-0.5">
                                    {openedPrescription.procedure ||
                                        "Sin procedimiento"}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => {
                                    setOpenedPrescription(null);
                                    setSelectedPrescription([]);
                                    setSelectedProcedure("all");
                                    setCurrentInstructions([]);
                                }}
                                className="
                                    rounded-lg
                                    bg-red-50
                                    px-3
                                    py-2
                                    text-xs
                                    font-medium
                                    text-red-500
                                    hover:bg-red-100
                                "
                            >
                                Cerrar
                            </button>

                        </div>
                    </div>

                )}

            </div>

            {selectedPrescription.length === 0 ? (

                <div className="
                    rounded-2xl
                    border
                    border-dashed
                    border-slate-200
                    bg-slate-50
                    p-8
                    text-center
                ">

                    <div className="mt-2 text-sm font-medium text-slate-700">
                        No hay medicamentos seleccionados
                    </div>

                    <div className="mt-1 text-xs text-slate-500">
                        Selecciona un medicamento del catálogo para comenzar la receta.
                    </div>

                </div>

                ) : (

                    selectedPrescription.map(
                    medication => (

                        <div
                        key={medication.id}
                        className="rounded-2xl border p-3"
                        >

                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <div className="text-sm font-semibold text-slate-900">
                                {medication.name}
                                </div>

                                <div className="mt-1 text-xs text-slate-500">
                                    {isPediatric && medication.pediatricPresentation
                                        ? `Presentación pediátrica: ${medication.pediatricPresentation}`
                                        : medication.adultPresentation ||
                                        medication.presentation ||
                                        "Sin presentación"}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                setSelectedPrescription(
                                    prev =>
                                    prev.filter(
                                        item => item.id !== medication.id
                                    )
                                )
                                }
                                className="
                                rounded-lg
                                px-2
                                py-1
                                text-xs
                                text-red-600
                                hover:bg-red-50
                                "
                            >
                                Quitar
                            </button>
                        </div>

                        <div className="mt-2">

                            <div className="text-xs text-gray-500">
                                Dosis
                            </div>

                            <input
                                value={
                                    medication.customDose ||
                                    calculateDose(
                                        medication
                                    )
                                }
                                onChange={e => {

                                    setSelectedPrescription(
                                        prev =>
                                            prev.map(
                                                item =>
                                                    item.id === medication.id
                                                        ? {
                                                            ...item,
                                                            customDose:
                                                                e.target.value
                                                        }
                                                        : item
                                            )
                                    );

                                }}
                                className="
                                    mt-1
                                    w-full
                                    rounded-lg
                                    border
                                    p-2
                                    text-sm
                                "
                            />

                        </div>

                        <div className="mt-3 grid grid-cols-3 gap-3">

                        <div>
                            <div className="text-xs font-medium text-gray-500">
                                Duración
                            </div>

                            <input
                                type="number"
                                min="1"
                                value={
                                    medication.customDuration ??
                                    medication.duration
                                }
                                onChange={e => {

                                    const value =
                                        Number(e.target.value);

                                    setSelectedPrescription(
                                        prev =>
                                            prev.map(
                                                item =>
                                                    item.id === medication.id
                                                        ? {
                                                            ...item,
                                                            customDuration:
                                                                value
                                                        }
                                                        : item
                                            )
                                    );

                                }}
                                className="
                                    mt-1
                                    w-full
                                    rounded-lg
                                    border
                                    p-2
                                    text-sm
                                "
                            />
                        </div>

                        <div>
                            <div className="text-xs font-medium text-gray-500">
                                Frecuencia
                            </div>

                            <input
                                value={
                                    medication.customInterval ??
                                    medication.interval
                                }
                                onChange={e => {

                                    setSelectedPrescription(
                                        prev =>
                                            prev.map(
                                                item =>
                                                    item.id === medication.id
                                                        ? {
                                                            ...item,
                                                            customInterval:
                                                                e.target.value
                                                        }
                                                        : item
                                            )
                                    );

                                }}
                                className="
                                    mt-1
                                    w-full
                                    rounded-lg
                                    border
                                    p-2
                                    text-sm
                                "
                            />
                        </div>

                        <div>
                            <div className="text-xs font-medium text-gray-500">
                                Vía
                            </div>

                            <select
                                value={
                                    medication.customRoute ??
                                    medication.route
                                }
                                onChange={e => {

                                    setSelectedPrescription(
                                        prev =>
                                            prev.map(
                                                item =>
                                                    item.id === medication.id
                                                        ? {
                                                            ...item,
                                                            customRoute:
                                                                e.target.value
                                                        }
                                                        : item
                                            )
                                    );

                                }}
                                className="
                                    mt-1
                                    w-full
                                    rounded-lg
                                    border
                                    p-2
                                    text-sm
                                "
                            >
                                {
                                    (
                                        medication.allowedRoutes ||
                                        [medication.route]
                                    ).map(route => (

                                        <option
                                            key={route}
                                            value={route}
                                        >
                                            {route}
                                        </option>

                                    ))
                                }
                            </select>
                        </div>

                    </div>

                        <div className="mt-3">

                            <label className="text-xs font-medium text-gray-500">
                                Indicaciones particulares
                            </label>

                            <textarea
                                value={
                                    medication.customInstructions || ""
                                }
                                onChange={e => {

                                    setSelectedPrescription(
                                        prev =>
                                            prev.map(
                                                item =>
                                                    item.id === medication.id
                                                        ? {
                                                            ...item,
                                                            customInstructions:
                                                                e.target.value
                                                        }
                                                        : item
                                            )
                                    );

                                }}
                                rows={2}
                                placeholder="Ej. Tomar después de los alimentos..."
                                className="
                                    mt-1
                                    w-full
                                    resize-none
                                    rounded-xl
                                    border
                                    border-slate-200
                                    bg-slate-50
                                    p-3
                                    text-sm
                                    outline-none
                                    focus:bg-white
                                "
                            />

                        </div>

                        </div>

                    )
                    )

                )
                }

                {/* INDICACIONES */}

                <div className="mt-6 rounded-2xl border bg-white p-4">

                    <div className="flex items-center justify-between">

                        <div>
                            <div className="text-sm font-semibold text-slate-900">
                                Indicaciones
                            </div>

                            <div className="mt-1 text-xs text-slate-500">
                                Recomendaciones asociadas al tratamiento
                            </div>
                        </div>

                        <div className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                            {currentInstructions.length}
                        </div>

                    </div>

                    <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">

                        <div className="flex items-center gap-1 border-b border-slate-200 bg-white px-2 py-2">

                            <button
                                type="button"
                                title="Lista con viñetas"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() =>
                                    instructionsEditor
                                        ?.chain()
                                        .focus()
                                        .toggleBulletList()
                                        .run()
                                }
                                className="
                                    flex
                                    h-8
                                    w-8
                                    items-center
                                    justify-center
                                    rounded-lg
                                    text-slate-600
                                    hover:bg-slate-100
                                "
                            >
                                <List size={16} />
                            </button>

                            <button
                                type="button"
                                title="Negrita"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() =>
                                    instructionsEditor
                                        ?.chain()
                                        .focus()
                                        .toggleBold()
                                        .run()
                                }
                                className="
                                    flex
                                    h-8
                                    w-8
                                    items-center
                                    justify-center
                                    rounded-lg
                                    font-bold
                                    text-slate-700
                                    hover:bg-slate-100
                                "
                            >
                                B
                            </button>

                            <button
                                type="button"
                                title="Cursiva"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() =>
                                    instructionsEditor
                                        ?.chain()
                                        .focus()
                                        .toggleItalic()
                                        .run()
                                }
                                className="
                                    flex
                                    h-8
                                    w-8
                                    items-center
                                    justify-center
                                    rounded-lg
                                    italic
                                    text-slate-700
                                    hover:bg-slate-100
                                "
                            >
                                I
                            </button>

                            <button
                                type="button"
                                title="Subrayado"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() =>
                                    instructionsEditor
                                        ?.chain()
                                        .focus()
                                        .toggleUnderline()
                                        .run()
                                }
                                className="
                                    flex
                                    h-8
                                    w-8
                                    items-center
                                    justify-center
                                    rounded-lg
                                    text-slate-700
                                    hover:bg-slate-100
                                "
                            >
                                <UnderlineIcon size={16} />
                            </button>

                        </div>

                        <EditorContent
                            editor={instructionsEditor}
                            className="
                                min-h-[110px]
                                px-3
                                py-3
                                text-sm
                                text-slate-700
                                outline-none

                                [&_.ProseMirror]:min-h-[110px]
                                [&_.ProseMirror]:outline-none

                                [&_.ProseMirror_ul]:list-disc
                                [&_.ProseMirror_ul]:pl-6

                                [&_.ProseMirror_li]:ml-1
                            "
                        />

                    </div>

                    <div className="mt-2 text-xs text-slate-400">
                        Puedes escribir indicaciones y aplicar formato con las herramientas.
                    </div>

                </div>

            {/* HISTORIAL */}

            <div className="mt-8 border-t pt-6">

                <div className="flex items-center justify-between">

                    <div>
                        <h3 className="text-sm font-semibold text-slate-900">
                            Historial
                        </h3>

                        <div className="mt-1 text-xs text-slate-500">
                            Recetas anteriores del paciente
                        </div>
                    </div>

                    <div className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                        {history.length}
                    </div>

                </div>

                <div
                    className="
                        mt-4
                        max-h-[320px]
                        space-y-2
                        overflow-y-auto
                        pr-1
                    "
                >

                    {history.length === 0 ? (

                        <div className="
                            rounded-xl
                            border
                            border-dashed
                            border-slate-200
                            bg-slate-50
                            p-4
                            text-center
                            text-xs
                            text-slate-500
                        ">
                            No hay recetas anteriores.
                        </div>

                    ) : (

                        history.map(item => (

                            <div
                                key={item.id}
                                className="
                                    rounded-xl
                                    border
                                    border-slate-200
                                    bg-white
                                    p-3
                                    transition
                                    hover:shadow-sm
                                "
                            >

                                <div className="flex items-start justify-between gap-3">

                                    <div className="min-w-0">

                                        <div className="text-sm font-medium text-slate-900">
                                            {item.procedure || "Receta"}
                                        </div>

                                        <div className="mt-1 text-xs text-slate-400">
                                            {
                                                item.createdAt?.toDate
                                                    ? item.createdAt
                                                        .toDate()
                                                        .toLocaleDateString(
                                                            "es-MX"
                                                        )
                                                    : ""
                                            }
                                        </div>

                                    </div>

                                </div>

                                <div className="mt-2 text-xs text-slate-500">
                                    {item.medications
                                        ?.map(med => med.name)
                                        .join(", ")}
                                </div>

                                <div className="mt-3 flex gap-2">

                                    <button
                                        type="button"
                                        onClick={() => {

                                            setOpenedPrescription(
                                                item
                                            );

                                            setSelectedPrescription(
                                                item.medications || []
                                            );

                                            setSelectedProcedure(
                                                item.procedure || ""
                                            );

                                            console.log(
                                                "Receta abierta:",
                                                item
                                            );

                                            console.log(
                                                "item.instructions:",
                                                item.instructions
                                            );

                                            setCurrentInstructions(
                                                item.instructions || []
                                            );

                                            if (item.pdfUrl) {

                                                setPreviewUrl(
                                                    item.pdfUrl
                                                );

                                                setPreviewFileName(
                                                    `${item.procedure || "Receta"}.pdf`
                                                );

                                                setPreviewOpen(
                                                    true
                                                );

                                            }

                                        }}
                                        className="
                                            flex-1
                                            rounded-lg
                                            bg-blue-600
                                            px-3
                                            py-2
                                            text-xs
                                            font-medium
                                            text-white
                                            hover:bg-blue-700
                                        "
                                    >
                                        Abrir
                                    </button>

                                    <button
                                        type="button"
                                        onClick={async () => {

                                            const confirmar =
                                                await requestVexaConfirm(
                                                    "¿Eliminar receta?"
                                                );

                                            if (!confirmar) return;

                                            deletePrescription(
                                                item.id
                                            );

                                        }}
                                        className="
                                            rounded-lg
                                            border
                                            border-red-200
                                            bg-white
                                            px-3
                                            py-2
                                            text-xs
                                            font-medium
                                            text-red-600
                                            hover:bg-red-50
                                        "
                                    >
                                        Eliminar
                                    </button>

                                </div>

                            </div>

                        ))

                    )}

                </div>

            </div>
            </div>
            </div>

          {/* PDF */}

            <div className="col-span-12">

            <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm min-h-0 lg:min-h-[900px] min-w-0">

                <div className="flex items-center justify-between gap-4">

                    <div>
                        <h2 className="text-sm font-semibold text-slate-900">
                            Vista previa
                        </h2>

                        <div className="mt-1 text-xs text-slate-500">
                            Así aparecerá la receta en el PDF
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={generatePDF}
                        disabled={generatingRecipe}
                        className="
                            shrink-0
                            rounded-xl
                            bg-blue-600
                            px-4
                            py-2
                            text-xs
                            font-semibold
                            text-white
                            shadow-sm
                            transition
                            hover:bg-blue-700
                            disabled:cursor-not-allowed
                            disabled:opacity-60
                        "
                    >
                        {generatingRecipe
                            ? "Creando receta..."
                            : "Generar receta"}
                    </button>

                </div>

                <div
                    ref={pdfRef}
                    className="
                        mt-5
                        w-full
                        rounded-2xl
                        border
                        bg-white
                        p-6
                        shadow-inner
                    "
                >

                <div className="border-b pb-4">

                    {
                    clinicData?.logoURL && (

                        <img
                        src={clinicData.logoURL}
                        alt="Logo clínica"
                        className="
                            mb-3
                            h-20
                            object-contain
                        "
                        />

                    )
                    }

                    <div className="text-xl font-bold text-slate-800">

                        {
                        clinicData?.nombreClinica ||
                        "Consultorio"
                        }

                </div>

                    <div className="mt-2 text-sm text-gray-500">

                        {
                        currentUser?.nombre ||
                        "Odontólogo"
                        }

                    </div>

                    <div className="text-sm text-gray-500">

                        Cédula Profesional: {

                        currentUser?.cedula ||

                        "Sin registrar"

                        }

                    </div>

                    <div className="mt-2 text-sm text-gray-500">

                    Fecha: {currentDate}

                    </div>

                </div>

                <div className="mt-6">

                    <div className="font-semibold">

                    Paciente

                    </div>

                    <div className="mt-2 text-sm">

                    {currentPatient.nombre}
                    </div>

                    <div className="text-sm text-gray-600">

                    {currentPatient.edad} años • {currentPatient.peso} kg

                    </div>

                </div>

                <div className="mt-6">

                    <div className="flex items-center justify-between">

                        <div>
                            <div className="text-sm font-semibold text-slate-900">
                                Receta para el paciente
                            </div>

                            <div className="mt-1 text-xs text-slate-400">
                                Indicaciones de uso
                            </div>
                        </div>

                        <div className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-medium text-blue-700">
                            Paciente
                        </div>

                    </div>

                    <div className="mt-3 rounded-2xl border bg-slate-50 p-4">

                        {
                            selectedPrescription.length === 0 ? (

                                <div className="text-sm text-slate-400">
                                    No hay medicamentos seleccionados
                                </div>

                            ) : (

                                <div className="space-y-3">

                                    {selectedPrescription.map(
                                        medication => (

                                            <div
                                                key={medication.id}
                                                className="rounded-xl bg-white p-3"
                                            >
                                                <pre
                                                    className="
                                                        whitespace-pre-wrap
                                                        font-sans
                                                        text-xs
                                                        leading-5
                                                        text-slate-700
                                                    "
                                                >
                                                    {
                                                        buildPatientText(
                                                            medication
                                                        )
                                                    }
                                                </pre>
                                            </div>

                                        )
                                    )}

                                </div>

                            )
                        }

                    </div>

                </div>

                <div className="mt-5">

                    <div className="text-sm font-semibold text-slate-900">
                        Indicaciones
                    </div>

                    <div className="mt-3 rounded-2xl border bg-slate-50 p-4">

                        {!instructionsHtml ? (

                            <div className="text-xs text-slate-400">
                                Sin indicaciones adicionales
                            </div>

                        ) : (

                            <div
                                className="
                                    text-xs
                                    leading-5
                                    text-slate-600

                                    [&_p]:mb-2
                                    [&_p:last-child]:mb-0

                                    [&_ul]:my-2
                                    [&_ul]:list-disc
                                    [&_ul]:pl-5

                                    [&_li]:mb-1
                                    [&_li:last-child]:mb-0

                                    [&_strong]:font-semibold
                                    [&_em]:italic
                                    [&_u]:underline
                                "
                                dangerouslySetInnerHTML={{
                                    __html: instructionsHtml
                                }}
                            />

                        )}

                    </div>

                </div>

                <div className="mt-5">

                    <div className="flex items-center justify-between">

                        <div>
                            <div className="text-sm font-semibold text-slate-900">
                                Receta para farmacia
                            </div>

                            <div className="mt-1 text-xs text-slate-400">
                                Presentación y dispensación
                            </div>
                        </div>

                        <div className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-medium text-slate-600">
                            Farmacia
                        </div>

                    </div>

                    <div className="mt-3 rounded-2xl border bg-slate-50 p-4">

                        {
                            selectedPrescription.length === 0 ? (

                                <div className="text-sm text-slate-400">
                                    No hay medicamentos seleccionados
                                </div>

                            ) : (

                                <div className="space-y-3">

                                    {selectedPrescription.map(
                                        medication => (

                                            <div
                                                key={`pharmacy-${medication.id}`}
                                                className="rounded-xl bg-white p-3"
                                            >
                                                <pre
                                                    className="
                                                        whitespace-pre-wrap
                                                        font-sans
                                                        text-xs
                                                        leading-5
                                                        text-slate-700
                                                    "
                                                >
                                                    {
                                                        buildPharmacyText(
                                                            medication
                                                        )
                                                    }
                                                </pre>
                                            </div>

                                        )
                                    )}

                                </div>

                            )
                        }

                    </div>

                </div>

                <div className="mt-12">

                    <div className="border-t pt-3 text-right text-sm">

                    Firma

                    </div>

                </div>

                <div className="mt-8 text-xs text-gray-500">

                    La prescripción final es responsabilidad del profesional tratante.

                </div>

                </div>

            </div>

            </div>

        {
            clinicalAlert && (

                <div
                className="
                fixed
                inset-0
                z-[999999]
                flex
                items-center
                justify-center
                bg-black/50
                "
                >

                <div
                    className="
                    w-[650px]
                    max-w-[90vw]
                    rounded-3xl
                    bg-white
                    p-6
                    shadow-2xl
                    "
                >

                    <div className="text-xl font-bold">

                    {
                        clinicalAlert.type === "blocked"
                        ? "🚫 Medicamento contraindicado"
                        : "⚠ Advertencia clínica"
                    }

                    </div>

                    <div className="mt-4 text-lg">

                    {
                        clinicalAlert.medication.name
                    }

                    </div>

                    <div className="mt-4 space-y-2">

                    {
                        clinicalAlert.warnings.map(
                        warning => (

                            <div
                            key={warning}
                            className="
                            rounded-xl
                            bg-slate-100
                            p-3
                            text-sm
                            "
                            >

                            {warning}

                            </div>

                        )
                        )
                    }

                    </div>

                    {
                        clinicalAlert.alternatives &&
                        clinicalAlert.alternatives.length > 0 && (

                        <div
                            className="
                            mt-4
                            rounded-xl
                            border
                            border-green-200
                            bg-green-50
                            p-4
                            "
                        >

                            <div
                            className="
                                mb-3
                                font-semibold
                                text-green-800
                            "
                            >

                            Alternativas sugeridas

                            </div>

                            {
                            [
                                ...new Set(
                                clinicalAlert.alternatives
                                )
                            ].map(
                                alternative => {

                                const alternativeMedication =
                                    availableMedications.find(
                                    med =>
                                        med.name === alternative
                                    );

                                return (

                                    <button
                                    key={alternative}
                                    onClick={() => {

                                        if (
                                        alternativeMedication
                                        ) {

                                        addMedicationDirect(
                                            alternativeMedication
                                        );

                                        }

                                        setClinicalAlert(
                                        null
                                        );

                                    }}
                                    className="
                                        w-full
                                        rounded-xl
                                        bg-green-100
                                        px-3
                                        py-2
                                        mb-2
                                        text-left
                                        text-sm
                                        text-green-800
                                        hover:bg-green-200
                                    "
                                    >

                                        ➕ Agregar {alternative}

                                    </button>

                                );

                                }
                            )
                            }

                        </div>

                        )
                    }

                    {
                    clinicalAlert.type === "warning" && (

                        <div className="mt-6 flex gap-3">

                        <button

                            onClick={() => {

                            addMedicationDirect(
                                clinicalAlert.medication
                            );

                            setClinicalAlert(
                                null
                            );

                            }}

                            className="
                            rounded-xl
                            bg-yellow-600
                            px-5
                            py-3
                            text-white
                            "
                        >

                            Agregar de todos modos

                        </button>

                        <button

                            onClick={() =>
                            setClinicalAlert(null)
                            }

                            className="
                            rounded-xl
                            bg-slate-300
                            px-5
                            py-3
                            "
                        >

                            Cancelar

                        </button>

                        </div>

                    )
                    }

                    {
                    clinicalAlert.type === "blocked" && (

                        <div className="mt-6">

                        <button

                            onClick={() =>
                            setClinicalAlert(null)
                            }

                            className="
                            rounded-xl
                            bg-red-600
                            px-5
                            py-3
                            text-white
                            "
                        >

                            Entendido

                        </button>

                        </div>

                    )
                    }

                </div>

                </div>

            )
            }

            <PdfViewerModal

                isOpen={
                    previewOpen
                }

                onClose={() => {

                    if (
                        previewUrl?.startsWith("blob:")
                    ) {
                        URL.revokeObjectURL(
                            previewUrl
                        );
                    }

                    setPreviewOpen(false);
                    setPreviewUrl("");
                    setPreviewFileName("");

                }}

                documentType="RECETA MÉDICA"

                title={
                    previewFileName ||
                    "Receta médica"
                }

                subtitle={
                    currentPatient?.nombre ||
                    ""
                }

                date={
                    new Date().toLocaleDateString(
                        "es-MX"
                    )
                }

                pdfUrl={
                    previewUrl
                }

                onWhatsApp={
                    () => {}
                }

                sendingWhatsApp={
                    false
                }

            />

        </div>

      </div>

    </div>

  );

}
